import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { ThemeId, DEFAULT_THEME, THEMES } from '../themes/themes';
import { getPref, setPref, onPreferencesChanged } from '../services/preferences';

// ── Shape ──────────────────────────────────────────────────────────────────

export interface ThemeContextValue {
  /** Currently active theme id */
  theme: ThemeId;
  /** Switch to a different theme — persists per user */
  setTheme: (id: ThemeId) => void;
  /** All available themes for building a picker UI */
  themes: typeof THEMES;
}

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * The theme is a GLOBAL preference on the server — one person, every company, every browser. It
 * is read from the in-memory bundle, so the first paint after a cold load uses the default until
 * the bundle arrives, and the subscription in the provider re-applies the stored choice the moment
 * it does. A brief flash on the first load of a new browser is the price of keeping nothing local.
 */
function readPersistedTheme(): ThemeId {
  const raw = getPref<string | null>('theme', null, 'global');
  return raw && THEMES.some(t => t.id === raw) ? (raw as ThemeId) : DEFAULT_THEME;
}

/** Applies the theme to <html data-theme="..."> immediately. */
function applyThemeToDOM(id: ThemeId): void {
  document.documentElement.setAttribute('data-theme', id);
}

/** Applies the persisted density preference to <html data-density="..."> on bootstrap. */
function applyPersistedDensity(): void {
  const v = getPref<string | null>('density', null, 'global');
  if (v === 'compact') document.documentElement.setAttribute('data-density', 'compact');
  else document.documentElement.removeAttribute('data-density');
}

// ── Context ────────────────────────────────────────────────────────────────

export const ThemeContext = createContext<ThemeContextValue>({
  theme: DEFAULT_THEME,
  setTheme: () => {},
  themes: THEMES,
});

// ── Provider ───────────────────────────────────────────────────────────────

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<ThemeId>(() => {
    const persisted = readPersistedTheme();
    // Apply immediately (before first paint) to avoid flash
    applyThemeToDOM(persisted);
    applyPersistedDensity();
    return persisted;
  });

  // Re-apply whenever theme changes (e.g. user switched mid-session)
  useEffect(() => {
    applyThemeToDOM(theme);
  }, [theme]);

  // The bundle can land after this provider initialised (cold load), and a change made in another
  // tab arrives on the next load of the bundle. Either way, re-read and re-apply.
  useEffect(() => onPreferencesChanged(() => {
    const next = readPersistedTheme();
    setThemeState(next);
    applyThemeToDOM(next);
    applyPersistedDensity();
  }), []);

  // Re-read persisted theme when user logs in/out (auth change event)
  useEffect(() => {
    const handler = () => {
      const next = readPersistedTheme();
      setThemeState(next);
      applyThemeToDOM(next);
    };
    window.addEventListener('auth-changed', handler);
    return () => window.removeEventListener('auth-changed', handler);
  }, []);

  const setTheme = useCallback((id: ThemeId) => {
    if (!THEMES.some(t => t.id === id)) return; // guard against invalid ids
    setThemeState(id);
    applyThemeToDOM(id);
    setPref('theme', id, 'global');
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, setTheme, themes: THEMES }),
    [theme, setTheme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
